import { useState, type ReactNode } from 'react';
import { IconCopy } from './icons';

/**
 * A phone number that can be copied, despite ScreenGuard.
 *
 * ScreenGuard blocks copying everywhere else; `data-allow-copy` is its one
 * exception, and the button writes through the clipboard API, which it does
 * not intercept. Clicks stop here so copying in the leads list does not also
 * open the lead.
 */
export function CopyPhone({ phone, children }: { phone: string | null; children?: ReactNode }) {
  const [copied, setCopied] = useState(false);
  if (!phone) return <span className="mono">—</span>;

  async function copy() {
    try {
      await navigator.clipboard.writeText(phone!);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (e.g. plain http): the number is still selectable */
    }
  }

  return (
    <span
      className="copy-phone"
      data-allow-copy
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <span className="mono">{children ?? phone}</span>
      <button
        type="button"
        className="copy-phone-btn"
        onClick={copy}
        title={copied ? 'Copied' : 'Copy number'}
        aria-label={copied ? 'Copied' : `Copy ${phone}`}
      >
        {copied ? <span className="copy-phone-done">Copied</span> : <IconCopy width={14} height={14} />}
      </button>
    </span>
  );
}
